import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, Lock } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { type AuthApp, type BetterAuthClient, signInWithGoogle } from '../../lib/betterAuthClient';
import { inviteAuthPath } from '../../lib/invite-links';
import { Alert, AlertDescription } from '../../ui/alert';
import { Button } from '../../ui/button';
import { PasswordInput } from '../../ui/password-input';
import { AgenticFlowByline } from '../agentic-flow-byline';
import { BrandLockup } from '../brand-lockup';
import { useInviteSignup } from './useInviteSignup';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export type LoginPageProps = {
  brandName: string;
  /** The product's mark, rendered in the brand lockup above the card. */
  logo: ReactNode;
  /** Which Better Auth instance this portal talks to (`/api/auth/{app}`). */
  app: AuthApp;
  authClient: BetterAuthClient;
};

/**
 * Shared sign-in form for both portals. Reached from an invite link (`?invite=<token>`) it
 * locks the email to the invited address for the same reason the register form does: accepting
 * the invite requires that exact account, so signing in as anyone else is a dead end.
 */
export function LoginPage({ brandName, logo, app, authClient }: LoginPageProps) {
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');
  const invite = useInviteSignup();
  const [error, setError] = useState<string | null>(
    searchParams.get('error_description') || (searchParams.get('error') ? 'Sign-in failed.' : null)
  );
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  });

  useEffect(() => {
    if (invite.email) setValue('email', invite.email);
  }, [invite.email, setValue]);

  const onSubmit = async (data: LoginFormValues) => {
    setError(null);
    setIsLoading(true);

    try {
      const { error: signInError } = await authClient.signIn.email({
        email: invite.email || data.email,
        password: data.password,
      });

      if (signInError) {
        setError(signInError.message || 'Failed to sign in. Please check your credentials.');
        return;
      }

      navigate(redirect || '/dashboard');
    } catch (err: unknown) {
      console.error('Login error:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to sign in. Please check your credentials.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const onGoogleSignIn = async () => {
    setError(null);
    try {
      const returnTo = `${window.location.origin}/oauth-complete?redirect=${encodeURIComponent(redirect || '/dashboard')}`;
      await signInWithGoogle(authClient, app, returnTo, {
        inviteToken: invite.token,
        loginHint: invite.email,
        errorCallbackURL: `${window.location.origin}/login${window.location.search}`,
      });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start Google sign-in.';
      setError(errorMessage);
    }
  };

  const emailLocked = Boolean(invite.email);

  return (
    <main className="min-h-screen flex flex-col">
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md space-y-6">
          <BrandLockup brandName={brandName} logo={logo} />
          <div className="space-y-8 bg-card p-8 rounded-xl border border-border shadow-sm">
            <div className="text-center">
              <h2 className="text-2xl font-bold tracking-tight">Welcome back</h2>
              <p className="text-sm text-muted-foreground mt-2">
                {invite.workspaceName
                  ? `Sign in to join ${invite.workspaceName}`
                  : `Sign in to your ${brandName} account`}
              </p>
            </div>

            {invite.isUnusable && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  That invitation is no longer valid — it may have expired or already been used. You
                  can still sign in normally.
                </AlertDescription>
              </Alert>
            )}

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  readOnly={emailLocked}
                  aria-readonly={emailLocked}
                  {...register('email')}
                  className={`w-full px-3 py-2 border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring ${
                    emailLocked ? 'cursor-not-allowed text-muted-foreground' : ''
                  }`}
                />
                {emailLocked ? (
                  <p className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                    <Lock className="h-3 w-3 shrink-0" />
                    The invitation was sent to this address — only this account can accept it.
                  </p>
                ) : (
                  errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="text-sm font-medium">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-xs font-medium text-primary hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
                <PasswordInput id="password" {...register('password')} />
                {errors.password && (
                  <p className="text-xs text-destructive">{errors.password.message}</p>
                )}
              </div>
              <Button
                className="w-full mt-4"
                type="submit"
                disabled={isLoading || invite.isLoading}
              >
                {isLoading ? 'Signing In...' : 'Sign In'}
              </Button>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">Or</span>
              </div>
            </div>

            <Button
              variant="outline"
              className="w-full"
              type="button"
              onClick={onGoogleSignIn}
              disabled={invite.isLoading}
            >
              Sign in with Google
            </Button>

            <div className="text-center text-sm">
              <span className="text-muted-foreground">Don&apos;t have an account? </span>
              <button
                type="button"
                onClick={() =>
                  navigate(
                    invite.token
                      ? inviteAuthPath('register', invite.token)
                      : `/register${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ''}`
                  )
                }
                className="font-medium text-primary hover:underline"
              >
                Sign up
              </button>
            </div>

            {emailLocked && (
              <div className="text-center text-xs">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-muted-foreground hover:underline"
                >
                  Sign in to a different account instead
                </button>
              </div>
            )}
          </div>
          <p className="text-center">
            <AgenticFlowByline />
          </p>
        </div>
      </div>
    </main>
  );
}
